import React, { useCallback, useEffect, useMemo } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { toast } from 'react-toastify';
import { AsyncSingletonError } from '@proscom/prostore-react';
import {
  addDays,
  addMonths,
  addYears,
  startOfToday,
  eachDayOfInterval,
  differenceInCalendarDays,
  compareAsc
} from 'date-fns';
import { isNil, min } from 'lodash-es';
import { useTimeoutRef } from '@proscom/ui-react';
import { VacationFormValidationSchema } from '../../../../store/validationSchema';
import {
  VacationAppProps,
  VacationsUtils
} from '../../../../store/vacations/VacationsUtils';
import {
  Title,
  TitleVariant
} from '../../../../common/components/ui/Title/Title';
import {
  VacationApplicationType,
  VacationScheduleType,
  PositionBalanceType,
  VacationStatusesEnum
} from '../../../../graphql/types';
import { Button } from '../../../../common/components/ui/Button';
import {
  HelpText,
  HelpTextType
} from '../../../../common/components/ui/HelpText/HelpText';
import { useGraphqlMutation } from '../../../../common/hooks/utils/useGraphqlMutation';
import { Vacations } from '../../../../store/vacations/Vacations';
import { handleDefaultError } from '../../../../utils/handleDefaultError';
import { getDateUtc } from '../../../../utils/date/date';
import { tryNumberLike } from '../../../../utils/number';
import {
  useDayOffs,
  useVacationDaysAmount
} from '../../../../common/hooks/useDayOffs';
import { Loader } from '../../../../common/components/ui/Loader/Loader';
import { ReactComponent as IconCheck } from '../../../../assets/img/icons/check.svg';
import { vacationMaxStartDate, vacationMinStartDate } from '../../constants';
import { VacationAppFormMain } from './VacationAppFormMain';
import { VacationAppFormCalendar } from './VacationAppFormCalendar';
import s from './VacationAppForm.module.scss';

const formStatus = {
  submitted: 'FORM_SUBMITTED'
};

/** Тип отпуска "По заявлению" */
export const VACATION_TYPE_MAIN = 1;
/** Тип отпуска "Без сохранения заработной платы" */
export const VACATION_TYPE_WITHOUT_PAY = 2;
/** Тип отпуска "Учебный без сохранения заработной платы" */
export const VACATION_TYPE_STUDY_WITHOUT_PAY = 3;
/** Тип отпуска "Учебный с сохранением заработной платы" */
export const VACATION_TYPE_STUDY_WITH_PAY = 4;
/** Тип отпуска "За вредные и (или) опасные условия труда" */
export const VACATION_TYPE_FOR_DANGEROUS_WORKING_CONDITIONS = 5;
/** Тип отпуска "Отпуск по беременности и родам" */
export const VACATION_TYPE_PREGNANCY = 6;
/** Тип отпуска "По уходу за ребенком до 3-х лет" */
export const VACATION_TYPE_CHILD_CARE = 7;
/** Тип отпуска "По уходу за детьми-инвалидами" */
export const VACATION_TYPE_DISABLED_CHILD_CARE = 8;
/** Тип отпуска "В связи со сдачей крови и ее компонентов" */
export const VACATION_TYPE_BLOOD_DONATION = 9;

/** Причина ухода в отпуск "По семейным обстоятельствам" */
export const VACATION_REASON_WITHOUT_PAY_FAMILY = 1;
/** Причина ухода в отпуск "Другое" */
export const VACATION_REASON_WITHOUT_PAY_OTHER = 2;

/** Тип отпуска в балансе отпускных дней для отпуска за вредные/опасные условия труда */
export const VACATION_BALANCE_TYPE_DANGEROUS =
  'За вредные и (или) опасные условия труда';

interface VacationAppFormCompProps extends FormikProps<VacationAppProps> {
  vacationsSchedules?: VacationScheduleType[] | null;
  vacationApps?: VacationApplicationType[];
  vacationBalances?: PositionBalanceType[] | null;
  submitLoading?: boolean;
}

const VacationAppFormComp: React.FC<VacationAppFormCompProps> = ({
  vacationsSchedules,
  vacationApps,
  vacationBalances,
  submitLoading,
  ...props
}) => {
  const { status, setStatus, submitForm, resetForm, setFieldValue } = props;
  const submitTO = useTimeoutRef();

  const values = props.values;
  const errors = props.errors;

  const positions = useMemo(() => {
    return vacationsSchedules?.map((v) => v.position) ?? [];
  }, [vacationsSchedules]);

  // проходим по всем заявлениям
  const vacationAppsExtended: VacationApplicationType[] = useMemo(() => {
    const tempVacationApps: VacationApplicationType[] = [];
    const vacationAppsFilteredByPosition = vacationApps?.filter(
      (vacationApp) => {
        return (
          values.position_id &&
          vacationApp.position.id === Number(values.position_id)
        );
      }
    );
    if (
      vacationAppsFilteredByPosition &&
      vacationAppsFilteredByPosition.length
    ) {
      vacationAppsFilteredByPosition.forEach((application) => {
        if (application.status === 'REJECTED') {
          return;
        }
        if (application.vacation_schedule_periods) {
          const vacationSchedulePeriods = JSON.parse(
            application.vacation_schedule_periods
          );
          const replacePlans = vacationSchedulePeriods.replace_plans;
          const newPlans = vacationSchedulePeriods.new_plans;

          // если это заявление на изменение, то вносим новые даты
          if (replacePlans && replacePlans.length) {
            replacePlans.forEach((replacePlan) => {
              const newPlans = replacePlan.new_plans;
              if (newPlans && newPlans.length) {
                newPlans.forEach((newPlan) => {
                  if (newPlan.start && newPlan.end) {
                    tempVacationApps.push(
                      Object.assign({}, application, {
                        start: newPlan.start,
                        end: newPlan.end
                      })
                    );
                  }
                });
              }
            });
          } else if (newPlans && newPlans.length) {
            newPlans.forEach((newPlan) => {
              if (newPlan.start && newPlan.end) {
                tempVacationApps.push(
                  Object.assign({}, application, {
                    start: newPlan.start,
                    end: newPlan.end
                  })
                );
              }
            });
          }
        } else {
          tempVacationApps.push(application);
        }
      });
    }

    return tempVacationApps;
  }, [vacationApps, values.position_id]);

  const selectedPositionId = tryNumberLike(values.position_id, null, true);

  const selectedVacationType = tryNumberLike(values.vacation_type, null, true);

  const selectedPosition = positions.find((p) => p.id === selectedPositionId);

  const isHeadPosition = !!selectedPosition?.is_head;
  const isVacationsDisabled = !!selectedPosition?.vacations_disabled;
  const canSubmit = useMemo(() => {
    if ([VACATION_TYPE_WITHOUT_PAY].includes(Number(values.vacation_type))) {
      const isNoPayReasonFilled =
        Number(values.no_pay_reason) === VACATION_REASON_WITHOUT_PAY_OTHER
          ? Boolean(values.no_pay_reason_text)
          : Boolean(values.no_pay_reason);

      return (
        selectedPosition && !Object.keys(errors).length && isNoPayReasonFilled
      );
    }

    if (
      [VACATION_TYPE_STUDY_WITHOUT_PAY, VACATION_TYPE_STUDY_WITH_PAY].includes(
        Number(values.vacation_type)
      )
    ) {
      return (
        selectedPosition &&
        !Object.keys(errors).length &&
        !!values.invitation_note
      );
    }

    if ([VACATION_TYPE_PREGNANCY].includes(Number(values.vacation_type))) {
      const isSickLeave =
        !!values.sick_leave_doc ||
        (values.digital_sick_leave_issued && !!values.sick_leave_number);
      return selectedPosition && !Object.keys(errors).length && isSickLeave;
    }

    if (
      [VACATION_TYPE_DISABLED_CHILD_CARE].includes(Number(values.vacation_type))
    ) {
      const isDisability = !values.disability_permanent
        ? !!values.disability_start && !!values.disability_end
        : true;

      return (
        selectedPosition &&
        !Object.keys(errors).length &&
        isDisability &&
        !!values.cert_other_parent_place &&
        !!values.cert_app_prev_place &&
        !!values.cert_other_parent_prev_place
      );
    }

    if (Number(values.vacation_type) === VACATION_TYPE_BLOOD_DONATION) {
      const isAbsent = !values.absent_from_work
        ? !!values.first_day_date && !!values.second_day_date
        : !!values.first_day_date;

      return (
        selectedPosition &&
        !Object.keys(errors).includes('deputy_id') &&
        isAbsent &&
        !!values.blood_donation_date &&
        !!values.reference &&
        !!values.reference_number
      );
    }
    return (
      selectedPosition && !isVacationsDisabled && !Object.keys(errors).length
    );
  }, [
    values.invitation_note,
    errors,
    selectedPosition,
    values.sick_leave_doc,
    values.digital_sick_leave_issued,
    values.sick_leave_number,
    values.cert_other_parent_place,
    values.cert_app_prev_place,
    values.cert_other_parent_prev_place,
    values.disability_permanent,
    values.disability_start,
    values.disability_end,
    values.absent_from_work,
    values.blood_donation_date,
    values.first_day_date,
    values.second_day_date,
    values.reference,
    values.reference_number,
    values.vacation_type,
    values.no_pay_reason,
    values.no_pay_reason_text,
    isVacationsDisabled
  ]);

  const daysAvailable = useMemo(() => {
    if (
      ![
        VACATION_TYPE_MAIN,
        VACATION_TYPE_FOR_DANGEROUS_WORKING_CONDITIONS
      ].includes(Number(values.vacation_type))
    ) {
      return 9999;
    }

    if (Number(values.vacation_type) === VACATION_TYPE_MAIN) {
      if (!vacationsSchedules?.length || isNil(selectedPositionId)) {
        return null;
      }
      return (
        vacationsSchedules.find((v) => v.position.id === selectedPositionId)
          ?.unassigned_days ?? null
      );
    } else if (
      Number(values.vacation_type) ===
      VACATION_TYPE_FOR_DANGEROUS_WORKING_CONDITIONS
    ) {
      if (!vacationBalances?.length || isNil(selectedPositionId)) {
        return null;
      }

      return (
        vacationBalances
          .find(
            (positionBalances) =>
              positionBalances.position_id === selectedPositionId
          )
          ?.balances.find(
            (positionBalance) =>
              positionBalance.vacation_type === VACATION_BALANCE_TYPE_DANGEROUS
          )?.amount ?? null
      );
    }

    return null;
  }, [
    vacationsSchedules,
    vacationBalances,
    selectedPositionId,
    values.vacation_type
  ]);

  const dangerousVacationAvailable = useMemo(() => {
    if (!vacationBalances?.length || isNil(selectedPositionId)) {
      return false;
    }

    return !!vacationBalances
      .find(
        (positionBalances) =>
          positionBalances.position_id === selectedPositionId
      )
      ?.balances.find(
        (positionBalance) =>
          positionBalance.vacation_type === VACATION_BALANCE_TYPE_DANGEROUS
      )?.amount;
  }, [vacationBalances, selectedPositionId]);

  const startDate = useMemo(() => {
    if (
      ![
        VACATION_TYPE_MAIN,
        VACATION_TYPE_FOR_DANGEROUS_WORKING_CONDITIONS,
        VACATION_TYPE_DISABLED_CHILD_CARE
      ].includes(Number(values.vacation_type))
    ) {
      return startOfToday();
    }

    return values.start ? new Date(getDateUtc(values.start)) : undefined;
  }, [values.start, values.vacation_type]);

  const holidayIntervalEndDate = useMemo(() => {
    return startDate ? addDays(startDate, daysAvailable || 0) : undefined;
  }, [startDate, daysAvailable]);

  const { daysOff, holidayDates, initialLoading } = useDayOffs({
    startDate: startDate ?? undefined,
    endDate: holidayIntervalEndDate
  });

  let selectedDaysAmount = useVacationDaysAmount({
    startDate: values.start,
    endDate: values.end
  });
  // для заявления "По уходу за детьми-инвалидами" можно выбрать дополнительные периоды отпуска
  let periodsDays = 0;
  if (
    values.disabled_child_vacation_periods &&
    values.disabled_child_vacation_periods.length
  ) {
    const periods = values.disabled_child_vacation_periods;
    periods.forEach((period) => {
      periodsDays += period.days ?? 0;
    });
  }
  if (selectedDaysAmount) {
    selectedDaysAmount += periodsDays;
  }

  const minStartDate = useMemo(() => {
    if (
      ![
        VACATION_TYPE_MAIN,
        VACATION_TYPE_FOR_DANGEROUS_WORKING_CONDITIONS
      ].includes(Number(values.vacation_type))
    ) {
      return startOfToday();
    }
    return vacationMinStartDate;
  }, [values.vacation_type]);

  const positionEndDate = selectedPosition?.c_end_date
    ? new Date(getDateUtc(selectedPosition.c_end_date))
    : undefined;

  const maxStartDate = min([positionEndDate, vacationMaxStartDate]);

  const minEndDate = useMemo(() => {
    if (
      ![
        VACATION_TYPE_MAIN,
        VACATION_TYPE_FOR_DANGEROUS_WORKING_CONDITIONS,
        VACATION_TYPE_DISABLED_CHILD_CARE
      ].includes(Number(values.vacation_type))
    ) {
      return startOfToday();
    }

    return startDate || vacationMinStartDate;
  }, [values.vacation_type, startDate]);

  const maxEndDate = useMemo(() => {
    let minDate: Date | undefined;
    if (values.start && vacationAppsExtended && vacationAppsExtended.length) {
      const mapped = vacationAppsExtended.map((application) => {
        return Object.assign({}, application, {
          end: new Date(getDateUtc(application.end)),
          start: new Date(getDateUtc(application.start))
        });
      });

      const maxEndDates: Date[] = [];
      // выбираем все заявления на отпуск, где дата конца больше выбранной стартовой даты
      mapped.forEach((application) => {
        if (values.start && application.end > new Date(values.start)) {
          maxEndDates.push(application.end);
        }
      });

      // среди них выбираем ближайшую к нашей стартовой дате
      minDate = new Date(
        Math.min(...maxEndDates.map((date) => date.getTime()))
      );
    }

    if (
      [VACATION_TYPE_WITHOUT_PAY, VACATION_TYPE_PREGNANCY].includes(
        Number(values.vacation_type)
      )
    ) {
      return positionEndDate;
    }

    if (
      [VACATION_TYPE_STUDY_WITHOUT_PAY, VACATION_TYPE_STUDY_WITH_PAY].includes(
        Number(values.vacation_type)
      )
    ) {
      const studyEndDate = startDate ? addMonths(startDate, 4) : undefined;
      return min([studyEndDate, positionEndDate]);
    }

    if ([VACATION_TYPE_CHILD_CARE].includes(Number(values.vacation_type))) {
      const childCareEndDate = values.date_of_birth
        ? addYears(new Date(values.date_of_birth), 3)
        : undefined;
      return min([childCareEndDate, positionEndDate]);
    }

    if (
      [VACATION_TYPE_DISABLED_CHILD_CARE].includes(Number(values.vacation_type))
    ) {
      let disabledChildEndDate: Date | undefined;

      if (
        selectedDaysAmount &&
        values.disabled_child_vacation_periods &&
        values.disabled_child_vacation_periods.length
      ) {
        const periods = values.disabled_child_vacation_periods;

        const lastPeriod = periods[periods.length - 1];
        let lastPeriodStart = lastPeriod.start;
        let lastPeriodDays = lastPeriod.days;
        // если у последнего периода не указаны даты, то берем предпоследний, если он есть
        if (!lastPeriod.start && periods.length > 1) {
          const previousPeriod = periods[periods.length - 2];
          lastPeriodStart = previousPeriod.start;
          lastPeriodDays = previousPeriod.days;
        }
        // высчитываем, на какую дату от начала последнего
        // периода максимально можно взять отпуск исходя из оставшихся дней
        // чтобы позволить пользователю "набрать" 24 дня независимо от начала-конца периодов
        const lastPeriodStartDate = new Date(getDateUtc(lastPeriodStart));

        // добавляем дни последнего периода, чтобы пользователь мог сдвинуть дату конца в последнем периоде
        const remainingDays = 23 - selectedDaysAmount + lastPeriodDays;

        disabledChildEndDate = addDays(lastPeriodStartDate, remainingDays);
      } else {
        disabledChildEndDate = startDate ? addDays(startDate, 23) : undefined;
      }

      return min([disabledChildEndDate, positionEndDate]);
    }

    const availEndDate = startDate
      ? addDays(startDate, (daysAvailable || 0) + daysOff.length - 1)
      : undefined;

    const availableDates = [availEndDate, maxStartDate, positionEndDate].filter(Boolean) as Date[];

    if (minDate) {
      availableDates.push(minDate);
    }

    return min(availableDates);
  }, [
    daysAvailable,
    daysOff,
    startDate,
    maxStartDate,
    values.vacation_type,
    values.date_of_birth,
    values.start,
    vacationAppsExtended,
    values.disabled_child_vacation_periods,
    selectedDaysAmount,
    positionEndDate
  ]);

  const formSubmitted = status === formStatus.submitted;

  useEffect(() => {
    if (formSubmitted) {
      resetForm({
        status: formStatus.submitted
      });
      submitTO.set(() => {
        setStatus(undefined);
      }, 3000);
    }
  }, [formSubmitted, setStatus, resetForm, submitTO]);

  useEffect(() => {
    setFieldValue('availableDaysAmount', daysAvailable);
  }, [daysAvailable, setFieldValue]);

  useEffect(() => {
    setFieldValue('selectedDaysAmount', selectedDaysAmount);
  }, [selectedDaysAmount, setFieldValue]);

  useEffect(() => {
    setFieldValue('isHeadPosition', isHeadPosition);
  }, [isHeadPosition, setFieldValue]);

  const blockingStatuses = [
    VacationStatusesEnum.Complete,
    VacationStatusesEnum.SentForApproval,
    VacationStatusesEnum.Registration
  ];

  let bookedDays = useMemo(() => {
    if (vacationAppsExtended && vacationAppsExtended.length) {
      const mapped = vacationAppsExtended
        .filter(
          (application) => blockingStatuses.includes(application.status)
        )
        .map((application) => {
          return Object.assign({}, application, {
            end: new Date(application.end),
            start: new Date(application.start)
          });
        });

      bookedDays = mapped
        ?.map((application) => {
          const result = eachDayOfInterval({
            start: application.start,
            end: application.end
          });

          return result;
        })
        .flat();
      bookedDays.sort(compareAsc);

      return bookedDays;
    }
  }, [vacationAppsExtended]);

  const minStartDateHint = useMemo(() => {
    let days;
    let availableDate;
    if (
      bookedDays &&
      bookedDays
        .map((bookedDay) => bookedDay.getTime())
        .includes(minStartDate.getTime())
    ) {
      const bookedDaysUnixTimes = bookedDays.map((day) => day.getTime());
      const interval = eachDayOfInterval({
        start: minStartDate,
        end: bookedDays[bookedDays.length - 1]
      });

      let nearestAvailableDate;
      for (let index = 0; index < interval.length; index++) {
        const day = interval[index];
        if (!bookedDaysUnixTimes.includes(day.getTime())) {
          nearestAvailableDate = day;
          break;
        }
      }

      // если забронирован вообще весь промежуток
      if (!nearestAvailableDate) {
        nearestAvailableDate = addDays(bookedDays[bookedDays.length - 1], 1);
      }

      if (nearestAvailableDate) {
        days = Math.abs(
          differenceInCalendarDays(startOfToday(), nearestAvailableDate)
        );
        availableDate = nearestAvailableDate.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit'
        });
      }
    } else {
      days = Math.abs(differenceInCalendarDays(startOfToday(), minStartDate));
      availableDate = minStartDate.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit'
      });
    }
    if (days === 0) {
      return 'отпуск доступен с текущей даты';
    } else if (days) {
      return `отпуск доступен через ${days} дней (от ${availableDate})`;
    }
  }, [bookedDays, minStartDate]);

  return (
    <Form className={s.VacationAppForm}>
      {(initialLoading && <Loader />) || (
        <>
          <Title
            className={s.VacationAppForm__title_mobile}
            variant={TitleVariant.h3}
            title={'Заявление на отпуск'}
          />

          <div className={s.VacationAppForm__grid}>
            <div className={s.VacationAppForm__gridMain}>
              <div className={s.VacationAppForm__main}>
                <Title
                  className={s.VacationAppForm__title}
                  variant={TitleVariant.h3}
                  title={'Заявление на отпуск'}
                />
                <VacationAppFormMain
                  minStartDate={minStartDate}
                  maxStartDate={maxStartDate}
                  minEndDate={minEndDate}
                  maxEndDate={maxEndDate}
                  excludeDates={bookedDays}
                  minStartDateHint={minStartDateHint}
                  positions={positions}
                  selectedPositionId={selectedPositionId}
                  selectedVacationType={selectedVacationType}
                  daysAvailable={daysAvailable}
                  selectedDaysAmount={selectedDaysAmount}
                  isVacationsDisabled={isVacationsDisabled}
                  dangerousVacationAvailable={dangerousVacationAvailable}
                  {...props}
                />
              </div>
            </div>

            <div className={s.VacationAppForm__gridCalendar}>
              <div className={s.VacationAppForm__calendar}>
                <VacationAppFormCalendar
                  minDate={minEndDate}
                  maxDate={maxEndDate}
                  daysAvailable={daysAvailable}
                  holidayDates={holidayDates}
                  vacationApps={vacationApps}
                  isVacationsDisabled={isVacationsDisabled}
                  excludeDates={bookedDays}
                  {...props}
                />
              </div>
            </div>
          </div>

          <div className={s.VacationAppForm__actions}>
            <Button
              className={s.VacationAppForm__actionButton}
              type={'button'}
              iconLeft={formSubmitted ? <IconCheck /> : undefined}
              loading={submitLoading}
              disabled={!canSubmit}
              onClick={canSubmit ? submitForm : undefined}
            >
              {formSubmitted ? 'Заявление отправлено' : 'Отправить заявление'}
            </Button>
            {selectedVacationType === VACATION_TYPE_BLOOD_DONATION && (
              <HelpText type={HelpTextType.hint}>
                После подачи заявления на сайте необходимо принести оригинал
                справки в отдел кадров,
                <br />
                иначе последующая подача заявления по данному виду отпуска будет
                заблокирована.
              </HelpText>
            )}
            {selectedVacationType === VACATION_TYPE_DISABLED_CHILD_CARE && (
              <HelpText type={HelpTextType.hint}>
                После подачи заявления необходимо принести документы в отдел
                кадров
              </HelpText>
            )}
          </div>
        </>
      )}
    </Form>
  );
};

export interface VacationAppFormProps
  extends Pick<
    VacationAppFormCompProps,
    'vacationsSchedules' | 'vacationApps' | 'vacationBalances'
  > {}

export const VacationAppForm: React.FC<VacationAppFormProps> = ({
  vacationsSchedules,
  vacationApps,
  vacationBalances
}) => {
  const leaveVacApp = useGraphqlMutation(Vacations.leaveVacationApplication);
  const leaveVacAppRun = leaveVacApp.run;
  const leaveVacAppLoading = leaveVacApp.loading;

  const handleSubmit = useCallback(
    (data, { setSubmitting, setStatus }: FormikHelpers<VacationAppProps>) => {
      setSubmitting(true);

      const input = VacationsUtils.fromForm(data);

      leaveVacAppRun({ variables: { input } })
        .then((result) => {
          if (result.data?.result) {
            setStatus(formStatus.submitted);
            toast.success('Заявление на отпуск отправлено на согласование');
          }
        })
        .catch((err) => {
          if (err instanceof AsyncSingletonError) return;
          handleDefaultError(
            err,
            'Произошла ошибка при отправке заявления на отпуск. Попробуйте снова'
          );
        })
        .then(() => {
          setSubmitting(false);
        });
    },
    [leaveVacAppRun]
  );

  const initialValues = useMemo(() => {
    return VacationsUtils.toForm();
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={VacationFormValidationSchema}
      onSubmit={handleSubmit}
    >
      {(props) => (
        <VacationAppFormComp
          vacationsSchedules={vacationsSchedules}
          vacationApps={vacationApps}
          vacationBalances={vacationBalances}
          submitLoading={leaveVacAppLoading}
          {...props}
        />
      )}
    </Formik>
  );
};
